import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Scale, ChevronRight, CheckCircle, AlertCircle, FileCheck, HelpCircle, Mail } from "lucide-react";
import { getTermsContent, getLocalTermsContent, getSeoSettings, getLocalSeoSettings } from "../api";
import FormattedText from "../components/FormattedText";
import SEO from "../components/SEO";

const TermsConditions = () => {
  const [content, setContent] = useState(() => getLocalTermsContent());
  const [seo, setSeo] = useState(() => getLocalSeoSettings());

  useEffect(() => {
    getTermsContent().then((data) => {
      if (data) setContent(data);
    }).catch(() => {});

    getSeoSettings().then((data) => {
      if (data) setSeo(data);
    }).catch(() => {});
  }, []);

  const title = content?.title || "Terms & Conditions";
  const lastUpdated = content?.lastUpdated || "September 2026";
  const contactEmail = content?.contactEmail || seo?.contactEmail || "support@minigolfalabama.com";
  const siteDomain = seo?.siteUrl ? seo.siteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "") : "minigolfalabama.com";
  const siteName = seo?.siteName || "Mini Golf Alabama";

  const introParagraphs = content?.introParagraphs && content.introParagraphs.length > 0
    ? content.introParagraphs
    : [
        `By accessing and using ${siteDomain} (the "Website" or "Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use or access the Website.`,
        `${siteName} provides an informational directory for discovering miniature golf, putt-putt, indoor golf lounges, and family fun destinations across Alabama and all 50 states.`
      ];

  const sections = content?.sections && content.sections.length > 0
    ? content.sections
    : [
        {
          title: "1. Directory Information & Verification Disclaimer",
          content: "While we strive to keep all course information, hours of operation, pricing, ratings, phone numbers, and addresses accurate and current, details can change without notice due to seasonal schedules, holidays, weather conditions, or venue policy updates. We strongly advise verifying operating hours and pricing directly with the venue before traveling."
        },
        {
          title: "2. Permitted Use & Intellectual Property",
          content: "You are granted a non-exclusive, revocable license to access and browse our directory for personal, non-commercial purposes. Reproduction or scraping of directory content without prior written consent is strictly prohibited."
        },
        {
          title: "3. User Submissions & Inquiries",
          content: "When submitting contact messages or listing updates, you agree to provide truthful and accurate information."
        },
        {
          title: "4. Limitation of Liability",
          content: `Under no circumstances shall ${siteName} or its operators be liable for any direct, indirect, incidental, or consequential damages resulting from your use of the Website.`
        }
      ];

  return (
    <div className="min-h-[70vh] bg-[#f8fafc]" data-testid="terms-conditions-page">
      <SEO
        title={`${title} | ${siteName}`}
        description={`Read the Terms and Conditions governing your use of ${siteName} (${siteDomain}).`}
        canonicalUrl={`https://${siteDomain}/terms-and-conditions`}
        ogType="website"
      />
      {/* Header Banner */}
      <div className="bg-[#181a20] text-white border-b border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <span className="text-[#e5243b] font-medium">{title}</span>
          </nav>
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/20 flex items-center justify-center">
              <Scale className="w-6 h-6 text-[#2563eb]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
                {title}
              </h1>
              <p className="mt-1 text-sm text-gray-400">
                Last updated: <span className="text-gray-200 font-medium">{lastUpdated}</span> · Governing usage of {siteDomain}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-12 shadow-sm space-y-10 text-gray-700 leading-relaxed text-[15px]">
          
          {/* Agreement to Terms */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2.5 mb-4">
              <FileCheck className="w-6 h-6 text-[#e5243b]" />
              Agreement to Terms
            </h2>
            <div className="space-y-3">
              {introParagraphs.map((p, idx) => (
                <FormattedText
                  key={idx}
                  text={p}
                  paragraphClassName="text-gray-700 leading-relaxed"
                  linkClassName="text-[#2563eb] underline font-semibold hover:text-blue-700"
                />
              ))}
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Dynamic Sections */}
          {sections.map((section, idx) => (
            <div key={idx} className="space-y-3">
              <h2 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2.5">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                {section.title}
              </h2>
              <FormattedText
                text={section.content}
                paragraphClassName="text-gray-700 leading-relaxed"
                linkClassName="text-[#2563eb] underline font-semibold hover:text-blue-700"
              />
              {idx < sections.length - 1 && <hr className="border-gray-100 mt-8" />}
            </div>
          ))}

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-2xl p-6 sm:p-8 mt-10">
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-400" />
              Questions About These Terms?
            </h3>
            <p className="text-gray-300 text-sm mb-4">
              For any legal questions, listing updates, or inquiries regarding these Terms & Conditions, please contact us.
            </p>
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563eb] bg-white px-4 py-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <Mail className="w-4 h-4" /> {contactEmail}
            </a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
